import React from 'react';
import { PropTypes } from 'prop-types';
import { MultiSelectField, SimpleSelectField } from '@unite-us/app-components';
import { FieldArray } from 'react-final-form-arrays';
import { Field } from 'react-final-form';
import moment from 'moment';
import timeOfDayOptions from 'common/utils/timeOfDayOptions';
import ButtonAddField from './ButtonAddField';
import ButtonRemoveField from './ButtonRemoveField';
import {
  DAYS_OF_THE_WEEK,
  TIME_FORMAT_SERVER,
  TIME_FORMAT_DISPLAY,
} from '../constants';
import normalizeHours from '../utils/normalizeHours';

const timeSlotsOptions = timeOfDayOptions().map(({ label }) => ({
  value: moment(label, TIME_FORMAT_SERVER).format(TIME_FORMAT_DISPLAY),
  label,
}));

const daysOptions = DAYS_OF_THE_WEEK.map((day) => ({
  value: day.toLowerCase(),
  label: day,
}));

const validateValues = (newValue, orgValues) => {
  if (!newValue || !orgValues?.hours?.length) {
    return undefined;
  }

  const daysWithOverlappingHours = normalizeHours.hoursOverlap(orgValues.hours);
  return daysWithOverlappingHours.find((day) => newValue.includes(day)) ?
    "Review your selections. Hours shouldn't overlap." :
    undefined;
};

const HoursRow = ({ fieldName, index, onRemove }) => (
  <div key={`org-fieldset-${fieldName}`} data-testid="hours_set_row" className="flex">
    <div className="w-4/12 mr-2" data-test-element={`hours_day_${index}`}>
      <Field name={`${fieldName}.day`} validate={validateValues}>
        {({ input, meta }) => (
          <MultiSelectField
            id={`org-hours-day-${index}`}
            placeholder="Select..."
            label="Hours of Operation"
            hideLabel={!!index}
            options={daysOptions}
            value={input.value || []}
            onChange={input.onChange}
            onBlur={input.onBlur}
            error={meta.touched ? meta.error : undefined}
          />
        )}
      </Field>
    </div>
    <div className="w-2/12 mr-3 self-end" data-test-element={`hours_opens_${index}`}>
      <Field name={`${fieldName}.opens`}>
        {({ input, meta }) => (
          <SimpleSelectField
            id={`org-hours-starts-${index}`}
            options={timeSlotsOptions}
            label="Opens"
            hideLabel
            value={input.value}
            onChange={input.onChange}
            onBlur={input.onBlur}
            error={meta.touched ? meta.error : undefined}
            searchable
          />
        )}
      </Field>
    </div>
    <div className="w-1/5 self-end" data-test-element={`hours_closes_${index}`}>
      <Field name={`${fieldName}.closes`}>
        {({ input, meta }) => (
          <SimpleSelectField
            id={`org-hours-ends-${index}`}
            label="Closes"
            options={timeSlotsOptions}
            hideLabel
            value={input.value}
            onChange={input.onChange}
            onBlur={input.onBlur}
            error={meta.touched ? meta.error : undefined}
            searchable
          />
        )}
      </Field>
    </div>
    <ButtonRemoveField
      onClick={onRemove}
      testId={`hours_set_trash_${index}`}
    />
  </div>
);

HoursRow.propTypes = {
  fieldName: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  onRemove: PropTypes.func.isRequired,
};

const HoursOfOperationsEdit = ({ name, push }) => (
  <div className="relative">
    <ButtonAddField
      data-test-element="add_hours"
      isPositioned
      label="Add Hours"
      onClick={() => push('hours', {})}
    />
    <FieldArray name={name}>
      {({ fields }) => (fields.length ? fields.map((fieldName, index) => (
        <HoursRow
          key={`org-fieldset-${fieldName}`}
          fieldName={fieldName}
          index={index}
          onRemove={() => fields.remove(index)}
        />
      )) : <div className="h-8" />)}
    </FieldArray>
  </div>
);

HoursOfOperationsEdit.propTypes = {
  name: PropTypes.string.isRequired,
  // Push method comes from the React Final Form library
  // For more information refer to react-final-form-arrays
  // documentation.
  push: PropTypes.func.isRequired,
};

export default HoursOfOperationsEdit;
